<template>
  <div class="supplier-root datagrid-root">
    <div class="content-block">
      <div class="headline-container">
        <div class="headline-item"><h2>Stamdata</h2></div>
        <div class="subheadline-item">&nbsp; - Suppliers</div>
      </div>
    </div>

    <div class="content-block">
      <div class="grid-button-container">
        <div class="flex-align-right">
          <dx-button @click="clearFilterAndSorting" icon="clearfilter" hint="Fjern filter og sortering"></dx-button>
        </div>
      </div>
      <dx-data-grid
        ref="dataGrid"
        id="supplierDataGrid"
        :data-source="datasource"
        height="75vh"
        :focused-row-key="focusedRowKey"
        @init-new-row="onInitNewRow"
        @row-removing="rowRemoving">

        <dx-selection mode="single"/>
        <dx-export :enabled="true" fileName="Suppliers" />
        <dx-filter-row :visible="true" />
        <dx-header-filter :visible="false" />
        <dx-grouping :auto-expand-all="false" />
        <dx-group-panel :visible="false" />
        <dx-state-storing :enabled="true" type="sessionStorage" storage-key="supplier-list" />
        <dx-scrolling mode="virtual" />
        <dx-paging :page-size="50" />

        <dx-editing
          :allow-updating="true"
          :allow-deleting="true"
          :allow-adding="true"
          :confirm-delete="false"
          mode="form">
          <dx-form
            :col-count="1"
            label-location="top"
            :showValidationSummary="true">
            <dx-simple-item data-field="name">
              <dx-label text="Navn" />
            </dx-simple-item>
            <dx-simple-item data-field="description" editor-type="dxTextArea">
              <dx-label text="Beskrivelse" />
            </dx-simple-item>
            <dx-simple-item data-field="groupName">
              <dx-label text="Gruppe" />
            </dx-simple-item>
            <dx-simple-item data-field="inputFormatId">
              <dx-label text="Import format" />
            </dx-simple-item>
            <dx-simple-item data-field="invoicingType">
              <dx-label text="Fakturatype" />
            </dx-simple-item>
            <dx-simple-item data-field="active">
              <dx-label label="Aktiv" />
            </dx-simple-item>
            <dx-simple-item data-field="activeForAccountMapping">
              <dx-label label="Aktiv for kontomapning" />
            </dx-simple-item>
          </dx-form>
        </dx-editing>

        <dx-column
          data-field="name"
          caption="Navn"
          data-type="string"
          :hiding-priority="7">
          <dx-required-rule />
        </dx-column>

        <dx-column
          data-field="description"
          caption="Beskrivelse"
          data-type="string"
          :hiding-priority="6">
        </dx-column>

        <dx-column
          data-field="groupName"
          caption="Gruppe"
          data-type="string"
          :hiding-priority="5"
          :width="180">
        </dx-column>

        <dx-column
          data-field="inputFormatId"
          caption="Import format"
          :hiding-priority="4"
          :width="180">
          <dx-grid-lookup
            :data-source="masterdata.importFormatList.data"
            value-expr="id"
            display-expr="name"
          />
          <dx-required-rule />
        </dx-column>

        <dx-column
          data-field="invoicingType"
          caption="Fakturatype"
          :hiding-priority="4"
          :width="180">
          <dx-grid-lookup
            :data-source="masterdata.supplierInvoiceTypeList.data"
            value-expr="id"
            display-expr="name"
          />
          <dx-required-rule />
        </dx-column>

        <dx-column
          data-field="active"
          caption="Aktiv"
          data-type="boolean"
          :hiding-priority="3"
          :width="130" />

        <dx-column
          data-field="activeForAccountMapping"
          caption="Aktiv for kontomapning"
          data-type="boolean"
          :hiding-priority="2"
          :width="180" />

        <dx-column
         type="buttons"
         :visible="false" />

        <dx-master-detail
          :enabled="false"
          template="detailTemplate" />

        <template #detailTemplate="{ data: row }">
          <ContentLoaderList v-if="!loadingDataCompleted" />
          <div v-else>
            <DetailView
              :row="row"
              :masterdata="masterdata"
              @onEditClick="onEditClick"
              @onDeleteClick="onDeleteClick" />
          </div>
        </template>
      </dx-data-grid>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import ContentLoaderList from '@/components/skeletonLoaders/content-loader-list.vue';
import DetailView from '@/modules/masterData/suppliers/components/detail-view.vue';
import BaseDataGrid from '@/components/common/base-datagrid.vue';
import useDatagrid from '@/modules/masterData/suppliers/composables/datagrid.composable.js';
import useMasterdata from '@/modules/masterData/suppliers/composables/masterdata.composable.js';

const activeEditorOptions = {
  text: 'Aktiv',
};

export default {
  name: 'MainSupplierModule',
  extends: BaseDataGrid,
  components: {
    DetailView,
    ContentLoaderList,
  },
  setup() {
    const {
      datasource,
      getDatasource,
      onInitNewRow,
    } = useDatagrid();

    const {
      masterdata,
      loadMasterdata,
    } = useMasterdata();

    getDatasource();
    loadMasterdata();

    const loadingDataCompleted = computed(() => masterdata.importFormatList.loadingCompleted
      && masterdata.supplierInvoiceTypeList.loadingCompleted);

    return {
      datasource,
      activeEditorOptions,
      masterdata,
      loadingDataCompleted,
      onInitNewRow,
    };
  },
  methods: {
    rowRemoving(e) {
      this.onRowRemoving(e, `Vil du slette denne supplier?<br/><b>${e.data.name}</b>`, 'Slet supplier');
    },
  },
};
</script>

<style lang="scss">
.supplier-root {
  .headline-container {
    display: flex;

    .headline-item {
      flex: 0 0 110px;

      h2 {
        margin-top: 0px;
      }
    }

    .subheadline-item {
      padding-top: 11px; font-size: 20px;
    }
  }

  .link:hover {
    cursor: pointer;
  }
}
</style>
